import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';

export default function AnalyticsDashboard({ onSwitchAgent }) {
  const [overview, setOverview] = useState(null);
  const [topics, setTopics] = useState([]);
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTopic, setSelectedTopic] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [ovRes, topRes, epRes] = await Promise.all([
        api.get('/api/analytics/overview'),
        api.get('/api/analytics/topics'),
        api.get('/api/analytics/episodes'),
      ]);
      setOverview(ovRes.data);
      setTopics(topRes.data.topics || []);
      setEpisodes(epRes.data.episodes || []);
    } catch (err) { console.error('Analytics fetch error:', err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const now = new Date().toISOString().replace('T', ' ').slice(0, 19) + ' UTC';

  if (loading) return (<div className="analytics-loading" data-testid="analytics-loading"><div className="analytics-loading-ring"></div><div className="analytics-loading-text">ANALYTICS</div><div className="analytics-loading-sub">Loading platform intelligence...</div></div>);

  return (
    <div className="analytics-app" data-testid="analytics-app">
      <div className="analytics-topbar" data-testid="analytics-topbar">
        <div className="analytics-topbar-logo" data-testid="analytics-logo">ANALYTICS</div>
        <div className="analytics-topbar-sep" />
        <div className="analytics-topbar-sub">CREATOR INTELLIGENCE</div>
        <div className="analytics-topbar-spacer" />
        <div className="analytics-topbar-metric"><span className="analytics-metric-val">{overview?.total_views || 0}</span><span className="analytics-metric-label">VIEWS</span></div>
        <div className="analytics-topbar-metric"><span className="analytics-metric-val">${overview?.total_revenue_usd?.toFixed(2) || '0.00'}</span><span className="analytics-metric-label">REVENUE</span></div>
        <div className="analytics-topbar-metric"><span className="analytics-metric-val">{overview?.platform_count || 0}</span><span className="analytics-metric-label">PLATFORMS</span></div>
        <div className="analytics-topbar-sep" />
        <div className="analytics-topbar-clock">{now}</div>
        <button className="analytics-topbar-refresh" onClick={fetchData} data-testid="analytics-refresh-btn">REFRESH</button>
        <button className="analytics-switch-btn" onClick={() => onSwitchAgent('nova')} data-testid="analytics-switch-nova">NOVA</button>
        <button className="analytics-switch-btn" onClick={() => onSwitchAgent('atlas')} style={{ color: '#059669', borderColor: '#05966930' }} data-testid="analytics-switch-atlas">ATLAS</button>
        <button className="analytics-switch-btn" onClick={() => onSwitchAgent('show')} data-testid="analytics-switch-show">SHOW</button>
      </div>
      <div className="analytics-body">
        {/* Left Panel */}
        <div className="analytics-left" data-testid="analytics-left-panel">
          <div className="analytics-section"><div className="analytics-section-label">REVENUE</div>
            <div className="analytics-stat-row"><span className="analytics-stat-label">Total</span><span className="analytics-stat-val">${overview?.total_revenue_usd?.toFixed(2) || '0.00'}</span></div>
            {overview?.latest_revenue && <div className="analytics-stat-row"><span className="analytics-stat-label">RPM</span><span className="analytics-stat-val">${overview.latest_revenue.avg_rpm?.toFixed(2) || '0.00'}</span></div>}
          </div>
          <div className="analytics-section"><div className="analytics-section-label">PERFORMANCE</div>
            <div className="analytics-stat-row"><span className="analytics-stat-label">Total Views</span><span className="analytics-stat-val">{overview?.total_views || 0}</span></div>
            <div className="analytics-stat-row"><span className="analytics-stat-label">Topics Tracked</span><span className="analytics-stat-val">{topics.length}</span></div>
            <div className="analytics-stat-row"><span className="analytics-stat-label">Episodes</span><span className="analytics-stat-val">{episodes.length}</span></div>
          </div>
          <div className="analytics-section"><div className="analytics-section-label">PLATFORMS</div>
            {(overview?.platforms || []).map((p, i) => (
              <div className="analytics-stat-row" key={i}><span className="analytics-stat-label">{p.platform}</span><span className="analytics-stat-val">{p.views}</span></div>
            ))}
            {(!overview?.platforms || overview.platforms.length === 0) && <div style={{ fontFamily: 'var(--an-mono)', fontSize: '9px', color: 'var(--an-text3)', letterSpacing: '1px' }}>Connect platform APIs to populate</div>}
          </div>
        </div>
        {/* Center Panel */}
        <div className="analytics-center" data-testid="analytics-center-panel">
          <div className="analytics-feed">
            {topics.length === 0 && episodes.length === 0 ? (
              <div className="analytics-empty" data-testid="analytics-empty">
                <div className="analytics-empty-text">AWAITING DATA</div>
                <div className="analytics-empty-sub">Connect YouTube, TikTok, or other platform APIs to see performance data. Analytics will populate as episodes and content are tracked.</div>
              </div>
            ) : (
              <>
                {topics.map((t, i) => (
                  <div className="analytics-card" key={i} onClick={() => setSelectedTopic(t)} data-testid={`analytics-topic-${i}`}>
                    <div className="analytics-card-header"><span className="analytics-card-title">{t.topic}</span><span className="analytics-card-metric">{t.total_views} views</span></div>
                    <div className="analytics-card-meta"><span>RPM: ${t.revenue_usd?.toFixed(2) || '0'}</span><span>Retention: {t.avg_retention_pct?.toFixed(0) || 0}%</span><span>Trend: {t.trend_direction}</span></div>
                  </div>
                ))}
                {episodes.map((e, i) => (
                  <div className="analytics-card" key={`ep-${i}`} data-testid={`analytics-episode-${i}`}>
                    <div className="analytics-card-header"><span className="analytics-card-title">{e.title || e.episode_id}</span><span className="analytics-card-metric">{e.views} views</span></div>
                    <div className="analytics-card-meta"><span>{e.platform}</span><span>Rev: ${e.revenue_usd?.toFixed(2) || '0'}</span><span>{e.format_type}</span></div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
        {/* Right Panel */}
        <div className="analytics-right" data-testid="analytics-right-panel">
          {selectedTopic ? (
            <div data-testid="analytics-detail">
              <div className="analytics-section-label">TOPIC DETAIL</div>
              <div className="analytics-detail-block">
                <div className="analytics-stat-row"><span className="analytics-stat-label">Topic</span><span className="analytics-stat-val">{selectedTopic.topic}</span></div>
                <div className="analytics-stat-row"><span className="analytics-stat-label">Views</span><span className="analytics-stat-val">{selectedTopic.total_views}</span></div>
                <div className="analytics-stat-row"><span className="analytics-stat-label">Revenue</span><span className="analytics-stat-val">${selectedTopic.revenue_usd?.toFixed(2)}</span></div>
                <div className="analytics-stat-row"><span className="analytics-stat-label">Retention</span><span className="analytics-stat-val">{selectedTopic.avg_retention_pct?.toFixed(0)}%</span></div>
                <div className="analytics-stat-row"><span className="analytics-stat-label">Episodes</span><span className="analytics-stat-val">{selectedTopic.episode_count}</span></div>
                <div className="analytics-stat-row"><span className="analytics-stat-label">Trend</span><span className="analytics-stat-val">{selectedTopic.trend_direction}</span></div>
              </div>
            </div>
          ) : (
            <div className="analytics-no-selection" data-testid="analytics-no-selection">SELECT TOPIC</div>
          )}
        </div>
      </div>
    </div>
  );
}
